import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';

import { type Components, type Theme } from '@mui/material';

import { themeColors } from '@/config/theme/themeColors.ts';
import { typography } from '@/config/theme/typography.ts';

declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsColorOverrides {
    white: true;
  }
}

export const components: Components<Omit<Theme, 'components'>> = {
  MuiAutocomplete: {
    styleOverrides: {
      inputRoot: {
        // Match MuiInput.root (TextFormField) metrics: Autocomplete's default
        // `paddingBottom: 1` and size=small input paddings made fields taller.
        '&.MuiInput-root': {
          paddingBottom: 8,
          paddingLeft: 12,
          paddingTop: 8,
        },
        '&.MuiInput-root .MuiInput-input': {
          padding: 0,
        },
        '&.MuiInput-root.MuiInputBase-sizeSmall .MuiInput-input': {
          padding: 0,
        },
      },
      tag: {
        // Single-line tags sized like the input text so multi selects keep
        // the same field height as single selects / TextFormField.
        '& .MuiChip-deleteIcon': {
          fontSize: '14px',
          margin: '0 0 0 4px',
        },
        '& .MuiChip-label': {
          padding: 0,
        },
        borderRadius: '8px',
        height: 20,
        margin: '0 2px',
        padding: '0 6px',
      },
    },
  },
  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f16Regular,
        backgroundColor: 'transparent',
        border: `1px solid ${theme.colors.stroke}`,
        color: theme.colors.grey,
      }),
    },
  },
  MuiButton: {
    defaultProps: {
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...theme.typography.f14Medium,
        ...(ownerState.size === 'small' && {
          ...theme.typography.f12Medium,
          minHeight: '28px',
          padding: '4px 12px',
        }),
        ...(ownerState.size === 'medium' && {
          ...theme.typography.f14Medium,
          minHeight: '36px',
          padding: '6px 16px',
        }),
        ...(ownerState.size === 'large' && {
          ...theme.typography.f16Medium,
          minHeight: '44px',
          padding: '8px 20px',
        }),
        borderRadius: '8px',
        boxShadow: 'none',
        fontWeight: 500,
        letterSpacing: '0.3px',
        textTransform: 'none',
        transition: 'all 0.2s ease-in-out',

        // Contained variant - Primary
        ...(ownerState.variant === 'contained' &&
          ownerState.color === 'primary' && {
            '&:active': {
              boxShadow: '0 2px 4px rgba(0, 91, 187, 0.2)',
              transform: 'translateY(0)',
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.colors.blue}`,
              outlineOffset: '2px',
            },
            '&:hover': {
              background: theme.colors.darkBlue,
              boxShadow: '0 4px 12px rgba(0, 91, 187, 0.3)',
              transform: 'translateY(-1px)',
            },
            '&.Mui-disabled': {
              background: theme.colors.lightGrey,
              color: theme.colors.disabledTextColor,
              opacity: 0.6,
            },
            background: theme.colors.blue,
            borderColor: theme.colors.blue,
            color: theme.colors.white,
          }),

        // Outlined variant - Primary
        ...(ownerState.variant === 'outlined' &&
          ownerState.color === 'primary' && {
            '&:active': {
              background: `${theme.colors.blue}20`,
              transform: 'translateY(0)',
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.colors.blue}`,
              outlineOffset: '2px',
            },
            '&:hover': {
              background: `${theme.colors.blue}10`,
              borderColor: theme.colors.darkBlue,
              boxShadow: '0 2px 8px rgba(0, 91, 187, 0.15)',
              color: theme.colors.darkBlue,
              transform: 'translateY(-1px)',
            },
            '&.Mui-disabled': {
              borderColor: theme.colors.lightGrey,
              color: theme.colors.disabledTextColor,
              opacity: 0.6,
            },
            background: theme.colors.white,
            border: `2px solid ${theme.colors.blue}`,
            color: theme.colors.blue,
          }),

        // Text variant - Primary
        ...(ownerState.variant === 'text' &&
          ownerState.color === 'primary' && {
            '&:active': {
              background: `${theme.colors.blue}20`,
            },
            '&:hover': {
              background: `${theme.colors.blue}10`,
              color: theme.colors.darkBlue,
            },
            '&.Mui-disabled': {
              color: theme.colors.disabledTextColor,
              opacity: 0.6,
            },
            color: theme.colors.blue,
            padding: '6px 12px',
          }),

        // Warning/Error color
        ...(ownerState.color === 'warning' && {
          ...(ownerState.variant === 'contained' && {
            '&:active': {
              transform: 'translateY(0)',
            },
            '&:hover': {
              backgroundColor: theme.colors.darkRed,
              boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.colors.red,
            color: theme.colors.white,
          }),
          ...(ownerState.variant === 'outlined' && {
            '&:hover': {
              background: `${theme.colors.red}10`,
              borderColor: theme.colors.darkRed,
              color: theme.colors.darkRed,
            },
            border: `2px solid ${theme.colors.red}`,
            color: theme.colors.red,
          }),
          ...(ownerState.variant === 'text' && {
            '&:hover': {
              background: `${theme.colors.red}10`,
            },
            color: theme.colors.red,
          }),
        }),

        // Success color
        ...(ownerState.color === 'success' && {
          ...(ownerState.variant === 'contained' && {
            '&:active': {
              transform: 'translateY(0)',
            },
            '&:hover': {
              backgroundColor: theme.colors.darkGreen,
              boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.colors.green,
            color: theme.colors.white,
          }),
          ...(ownerState.variant === 'outlined' && {
            '&:hover': {
              background: `${theme.colors.green}10`,
              borderColor: theme.colors.darkGreen,
              color: theme.colors.darkGreen,
            },
            border: `2px solid ${theme.colors.green}`,
            color: theme.colors.green,
          }),
          ...(ownerState.variant === 'text' && {
            '&:hover': {
              background: `${theme.colors.green}10`,
            },
            color: theme.colors.green,
          }),
        }),

        // Info color
        ...(ownerState.color === 'info' && {
          ...(ownerState.variant === 'contained' && {
            '&:active': {
              transform: 'translateY(0)',
            },
            '&:hover': {
              backgroundColor: theme.colors.darkGrey,
              boxShadow: '0 4px 12px rgba(108, 117, 125, 0.3)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.colors.grey,
            color: theme.colors.white,
          }),
          ...(ownerState.variant === 'outlined' && {
            '&:hover': {
              background: `${theme.colors.grey}10`,
              borderColor: theme.colors.darkGrey,
              color: theme.colors.darkGrey,
            },
            border: `2px solid ${theme.colors.grey}`,
            color: theme.colors.grey,
          }),
          ...(ownerState.variant === 'text' && {
            '&:hover': {
              background: `${theme.colors.grey}10`,
            },
            color: theme.colors.grey,
          }),
        }),
      }),
    },
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '&.MuiSwitch-switchBase': {
          '&.Mui-checked+.MuiSwitch-track': {
            backgroundColor: theme.colors.turquoise,
            opacity: 1,
          },
        },
      }),
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          color: theme.colors.primary,
        },
        '&.Mui-checked': {
          '&:hover': {
            color: theme.colors.oceanBlue,
          },
          color: theme.colors.primary,
        },
        '&.MuiCheckbox-indeterminate': {
          color: theme.colors.primary,
        },
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      label: {
        ...typography.f12Medium,
        fontWeight: 500,
        letterSpacing: '0.3px',
        padding: '0 4px',
      },
      root: ({ ownerState, theme }) => ({
        borderRadius: '8px',
        fontWeight: 500,
        height: 'auto',
        padding: '4px 8px',
        transition: 'all 0.2s ease',

        // Primary color (filled is default)
        ...(ownerState.color === 'primary' &&
          (!ownerState.variant || ownerState.variant === 'filled') && {
            '&:active': {
              transform: 'translateY(0)',
            },
            '&:hover': {
              backgroundColor: theme.colors.darkBlue,
              boxShadow: '0 2px 6px rgba(0, 91, 187, 0.25)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          }),

        // Outlined variant - Primary
        ...(ownerState.color === 'primary' &&
          ownerState.variant === 'outlined' && {
            '&:active': {
              backgroundColor: `${theme.colors.blue}20`,
              transform: 'translateY(0)',
            },
            '&:hover': {
              backgroundColor: `${theme.colors.blue}10`,
              borderColor: theme.colors.darkBlue,
              boxShadow: '0 2px 6px rgba(0, 91, 187, 0.15)',
              color: theme.colors.darkBlue,
              transform: 'translateY(-1px)',
            },
            backgroundColor: 'transparent',
            border: `1.5px solid ${theme.palette.primary.main}`,
            color: theme.palette.primary.main,
          }),

        // Default color (filled is default)
        ...(ownerState.color === 'default' &&
          (!ownerState.variant || ownerState.variant === 'filled') && {
            '&:hover': {
              backgroundColor: theme.colors.grey,
              boxShadow: '0 2px 6px rgba(108, 117, 125, 0.25)',
              color: theme.colors.white,
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.colors.lightGrey,
            color: theme.colors.darkGrey,
          }),

        // Default color - Outlined
        ...(ownerState.color === 'default' &&
          ownerState.variant === 'outlined' && {
            '&:hover': {
              backgroundColor: `${theme.colors.grey}10`,
              borderColor: theme.colors.darkGrey,
              boxShadow: '0 2px 6px rgba(108, 117, 125, 0.15)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: 'transparent',
            border: `1.5px solid ${theme.colors.grey}`,
            color: theme.colors.darkGrey,
          }),

        // Success color (filled is default)
        ...(ownerState.color === 'success' &&
          (!ownerState.variant || ownerState.variant === 'filled') && {
            '&:hover': {
              backgroundColor: theme.colors.darkGreen,
              boxShadow: '0 2px 6px rgba(40, 167, 69, 0.25)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.colors.green,
            color: theme.colors.white,
          }),

        // Success color - Outlined
        ...(ownerState.color === 'success' &&
          ownerState.variant === 'outlined' && {
            '&:hover': {
              backgroundColor: `${theme.colors.green}10`,
              borderColor: theme.colors.darkGreen,
              boxShadow: '0 2px 6px rgba(40, 167, 69, 0.15)',
              color: theme.colors.darkGreen,
              transform: 'translateY(-1px)',
            },
            backgroundColor: 'transparent',
            border: `1.5px solid ${theme.colors.green}`,
            color: theme.colors.green,
          }),

        // Warning color (filled is default)
        ...(ownerState.color === 'warning' &&
          (!ownerState.variant || ownerState.variant === 'filled') && {
            '&:hover': {
              backgroundColor: theme.colors.darkRed,
              boxShadow: '0 2px 6px rgba(220, 53, 69, 0.25)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.colors.red,
            color: theme.colors.white,
          }),

        // Warning color - Outlined
        ...(ownerState.color === 'warning' &&
          ownerState.variant === 'outlined' && {
            '&:hover': {
              backgroundColor: `${theme.colors.ocd}10`,
              borderColor: theme.colors.ocdDark,
              boxShadow: '0 2px 6px rgba(220, 53, 69, 0.15)',
              color: theme.colors.ocdDark,
              transform: 'translateY(-1px)',
            },
            backgroundColor: 'transparent',
            border: `1.5px solid ${theme.colors.ocd}`,
            color: theme.colors.ocd,
          }),

        // Error color (filled is default)
        ...(ownerState.color === 'error' &&
          (!ownerState.variant || ownerState.variant === 'filled') && {
            '&:hover': {
              backgroundColor: theme.colors.darkRed,
              boxShadow: '0 2px 6px rgba(220, 53, 69, 0.25)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.colors.red,
            color: theme.colors.white,
          }),

        // Error color - Outlined
        ...(ownerState.color === 'error' &&
          ownerState.variant === 'outlined' && {
            '&:hover': {
              backgroundColor: `${theme.colors.red}10`,
              borderColor: theme.colors.darkRed,
              boxShadow: '0 2px 6px rgba(220, 53, 69, 0.15)',
              color: theme.colors.darkRed,
              transform: 'translateY(-1px)',
            },
            backgroundColor: 'transparent',
            border: `1.5px solid ${theme.colors.red}`,
            color: theme.colors.red,
          }),

        // Info color (filled is default)
        ...(ownerState.color === 'info' &&
          (!ownerState.variant || ownerState.variant === 'filled') && {
            '&:hover': {
              backgroundColor: theme.colors.darkGrey,
              boxShadow: '0 2px 6px rgba(108, 117, 125, 0.25)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: theme.colors.grey,
            color: theme.colors.white,
          }),

        // Info color - Outlined
        ...(ownerState.color === 'info' &&
          ownerState.variant === 'outlined' && {
            '&:hover': {
              backgroundColor: `${theme.colors.grey}10`,
              borderColor: theme.colors.darkGrey,
              boxShadow: '0 2px 6px rgba(108, 117, 125, 0.15)',
              transform: 'translateY(-1px)',
            },
            backgroundColor: 'transparent',
            border: `1.5px solid ${theme.colors.grey}`,
            color: theme.colors.darkGrey,
          }),

        // Clickable chips
        ...(ownerState.onClick && {
          '&:active': {
            boxShadow: 'none',
            transform: 'translateY(0)',
          },
          cursor: 'pointer',
        }),

        // Deletable chips
        '& .MuiChip-deleteIcon': {
          '&:hover': {
            opacity: 0.7,
          },
          fontSize: '18px',
          margin: '0 -4px 0 4px',
          transition: 'all 0.2s ease',
        },
      }),
    },
  },
  MuiCircularProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.colors.blue,
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        maxWidth: 960,
        width: 960,
      },
      root: {
        margin: '0 auto',
        maxWidth: 960,
        width: '100%',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        paddingTop: '2px !important',
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.colors.stroke,
        marginBottom: 16,
        marginTop: 16,
      }),
    },
  },
  MuiFab: {
    styleOverrides: {
      root: ({ ownerState, theme }) => ({
        ...theme.typography.f14Medium,
        ...(ownerState.size === 'small' && {
          ...theme.typography.f12Regular,
          padding: '8px 16px',
        }),
        ...(ownerState.size === 'medium' && {
          ...theme.typography.f14Regular,
        }),
        ...(ownerState.size === 'large' && {
          ...theme.typography.f16Regular,
        }),
        '&:focus': {
          background: theme.colors.blue,
          borderColor: theme.colors.darkBlue,
        },
        '&:hover': {
          background: theme.colors.darkBlue,
          boxShadow: 'none',
          shadow: 'none',
        },
        '&.Mui-disabled': {
          background: theme.colors.lightGrey,
          borderColor: theme.colors.lightGrey,
        },
        background: theme.colors.blue,
        borderColor: theme.colors.blue,
        borderRadius: '8px',
        boxShadow: 'none',
        color: theme.colors.white,
        shadow: 'none',
        textTransform: 'capitalize',
        ...(ownerState.color === 'warning' && {
          '&:hover': {
            backgroundColor: theme.colors.darkRed,
            borderColor: theme.colors.darkRed,
            boxShadow: 'none',
            shadow: 'none',
          },
          backgroundColor: theme.colors.red,
          borderColor: theme.colors.red,
        }),
        ...(ownerState.color === 'success' && {
          '&:hover': {
            backgroundColor: theme.colors.darkGreen,
            borderColor: theme.colors.darkGreen,
          },
          backgroundColor: theme.colors.green,
          borderColor: theme.colors.green,
        }),
        ...(ownerState.color === 'info' && {
          '&:hover': {
            backgroundColor: theme.colors.darkGrey,
            borderColor: theme.colors.darkGrey,
          },
          backgroundColor: theme.colors.grey,
          borderColor: theme.colors.grey,
        }),
      }),
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        '& .MuiPickersInputBase-root:hover .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '& .MuiPickersInputBase-root.Mui-disabled .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '& .MuiPickersInputBase-root.Mui-focused .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        '& .MuiPickersOutlinedInput-notchedOutline': {
          borderColor: 'transparent',
        },
        borderRadius: '8px',
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
      label: ({ theme }) => ({
        ...theme.typography.f12Regular,
      }),
    },
  },
  MuiFormLabel: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.Mui-focused': {
          color: theme.colors.darkGrey,
        },
        color: theme.colors.darkGrey,
        transform: 'none',
      }),
    },
  },
  MuiInput: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&::placeholder': {
          ...theme.typography.f14Regular,
          color: theme.colors.placeholderColor,
        },
        padding: 0,
      }),
      root: ({ theme }) => ({
        ...theme.typography.f14Regular,
        '&::before': {
          borderBottom: 'none',
        },
        '&:hover:not(.Mui-disabled, .Mui-error)': {
          '&::before': {
            borderBottom: 'none',
          },
        },
        '&:hover:not(.Mui-focused):not(.Mui-error)': {
          borderColor: theme.colors.hoverBorderColor,
        },
        '&.Mui-disabled': {
          '&::before': {
            borderBottom: 'none',
          },
        },
        border: `1px solid ${theme.colors.stroke}`,
        padding: '8px 12px',
      }),
    },
  },
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }) => ({
        '&.MuiInput-input': {
          '&::placeholder': {
            opacity: 1,
          },
          '&.Mui-disabled': {
            '-webkit-text-fill-color': theme.colors.grey,
          },
        },
        '&.MuiOutlinedInput-input': {
          '&::placeholder': {
            ...theme.typography.f14Regular,
            color: theme.colors.placeholderColor,
            opacity: 1,
          },
          '&.Mui-disabled': {
            '-webkit-text-fill-color': theme.colors.grey,
          },
          '&.Mui-error': {
            borderColor: theme.colors.blue,
          },
          '&.Mui-focused': {
            borderColor: theme.colors.blue,
          },
          padding: '0px',
        },
      }),
      root: ({ theme }) => ({
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            '&:hover': {
              border: 'none',
            },
            border: 'none',
          },
        },
        '&.MuiInput-root': {
          '&.Mui-disabled': {
            backgroundColor: theme.colors.disabledBackgroundColor,
            webkitTextFillColor: theme.colors.disabledTextColor,
          },
          '&.Mui-error': {
            borderColor: theme.colors.red,
          },
          '&.Mui-focused': {
            borderColor: theme.colors.blue,
          },
        },
        '&.MuiOutlinedInput-root': {
          '&.Mui-disabled': {
            backgroundColor: theme.colors.disabledBackgroundColor,
            webkitTextFillColor: theme.colors.disabledTextColor,
          },
          '&.Mui-error': {
            borderColor: theme.colors.red,
          },
        },
        borderRadius: '4px',
        padding: '8px 12px',
      }),
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      asterisk: ({ theme }) => ({
        color: theme.colors.red,
      }),
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.Mui-disabled': {
          color: theme.colors.darkGrey,
        },
        '&.Mui-focused': {
          color: theme.colors.darkGrey,
        },
        color: theme.colors.darkGrey,
        display: 'flex',
        top: -4,
        transform: 'none',
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      bar: {
        backgroundColor: themeColors.turquoise,
        borderRadius: '100px',
      },
      root: {
        backgroundColor: themeColors.lightGrey,
        borderRadius: '100px',
        height: '8px',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      notchedOutline: {
        border: 'none',
      },
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&:hover:not(.Mui-focused)': {
          borderColor: theme.colors.hoverBorderColor,
        },
        '&.Mui-focused': {
          color: theme.colors.black,
        },
        border: `1px solid ${theme.colors.stroke}`,
        borderRadius: 4,
        color: theme.colors.black,
        transform: 'none',
      }),
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 2,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
        '&.MuiAccordion-root': {
          '&::before': {
            height: 0,
          },
          '&:last-of-type, :first-of-type': {
            borderRadius: 8,
          },
          '&.Mui-expanded': {
            margin: 0,
          },
          borderRadius: 8,
        },
        borderRadius: 8,
      }),
    },
  },
  MuiPickersInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.Mui-disabled': {
          backgroundColor: theme.colors.disabledBackgroundColor,
          borderColor: theme.colors.greyLight,
          cursor: 'default',
        },
      }),
    },
  },
  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:active': { borderColor: theme.colors.blue },
        '&:hover:not(.Mui-disabled)': { borderColor: theme.colors.grey },
        border: `1px solid ${theme.colors.greyLight}`,
        borderRadius: '4px',
      }),
      sectionsContainer: {
        paddingBottom: '7px',
        paddingTop: '7px',
      },
    },
  },
  MuiPickersSectionList: {
    styleOverrides: {
      sectionContent: {
        fontSize: '14px',
      },
    },
  },
  MuiRadio: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          color: theme.colors.darkBlue,
        },
        '&.Mui-checked': {
          color: theme.colors.blue,
        },
        '&.Mui-disabled': {
          color: theme.colors.black,
        },
        color: theme.colors.grey,
      }),
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        padding: '8px 12px',
      },
    },
  },
  MuiSlider: {
    styleOverrides: {
      root: {
        '&.Mui-disabled': {
          cursor: 'not-allowed',
        },
        color: themeColors.lightGrey,
      },
      thumb: {
        '&.Mui-disabled': {
          border: `6px solid ${themeColors.lightGrey}`,
          cursor: 'not-allowed',
        },
        border: `6px solid ${themeColors.turquoise}`,
        color: themeColors.white,
      },
    },
  },
  MuiStack: {
    defaultProps: {
      useFlexGap: true,
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      root: {
        fontSize: '20px',
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      root: {},
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.Mui-selected': {
          border: `1px solid ${theme.colors.turquoise}`,
          color: theme.colors.turquoise,
        },
        border: 'none',
        borderRadius: 8,
        color: theme.colors.darkGrey,
        minHeight: '12px',
        padding: '8px 16px',
      }),
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        borderCollapse: 'separate',
        borderSpacing: 0,
      },
    },
  },
  MuiTableBody: {
    styleOverrides: {
      root: {
        '& .MuiTableRow-root': {
          '&:hover': {
            backgroundColor: (theme: Theme) => theme.palette.action.hover,
            boxShadow: (theme: Theme) => `0 2px 8px ${theme.palette.action.hover}`,
          },
          '&:last-child .MuiTableCell-root': {
            borderBottom: 'none',
          },
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...theme.typography.f12Regular,
        '&.MuiTableCell-body': {
          '&:first-of-type': {
            color: theme.colors.turquoise,
          },
        },
        '&.MuiTableCell-head': {
          '&:first-of-type': {
            borderTopLeftRadius: '8px',
          },
          '&:last-of-type': {
            borderTopRightRadius: '8px',
          },
          borderBottom: `2px solid ${theme.palette.divider}`,
          color: theme.colors.coolGrey,
          fontSize: '0.625rem',
          fontWeight: 600,
          letterSpacing: '0.5px',
          padding: '8px 12px',
          textTransform: 'uppercase',
        },
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: '0.875rem',
        hyphens: 'auto',
        overflowWrap: 'break-word',
        padding: '12px 16px',
      }),
    },
  },
  MuiTableSortLabel: {
    styleOverrides: {
      root: {
        '&.MuiTableSortLabel-root': {
          '&:hover': {
            color: 'inherit',
            opacity: 0.9,
          },
          '&.Mui-active': {
            '& .MuiTableSortLabel-icon': {
              color: 'inherit',
              opacity: 1,
            },
            color: 'inherit',
          },
          color: 'inherit',
          fontWeight: 600,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        '& .MuiTabs-indicator': { background: themeColors.turquoise },
        '& .MuiTabs-list': {
          paddingBottom: '2px',
        },
      },
    },
  },
};
